import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { Users, Pencil, ArrowLeft, Mail, Phone, MapPin, DollarSign } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ViewVehicleOwner = () => {
  const { vehicleOwnerId } = useParams();
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOwner();
  }, [vehicleOwnerId, navigate]);

  const fetchOwner = async () => {
    try {
      const response = await vehicleOwnerService.getById(vehicleOwnerId);
      setOwner(response.data || response);
    } catch (err) {
      Alert.error('Failed to load owner details');
      console.error(err);
      navigate('/vehicle-owners');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading owner details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!owner) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto w-full min-w-0">
          <button
            onClick={() => navigate('/vehicle-owners')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Vehicle Owners
          </button>
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <p className="text-gray-600">Vehicle owner not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto w-full min-w-0">
        <button
          onClick={() => navigate('/vehicle-owners')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Vehicle Owners
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-buttonBg flex-shrink-0" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-secondary">{owner.owner_name}</h1>
                <p className="text-gray-600 text-sm sm:text-base">Owner ID: {owner.vehicle_owner_id}</p>
              </div>
            </div>
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() => navigate(`/vehicle-owners/edit/${vehicleOwnerId}`)}
            >
              Edit Owner
            </Button>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4 flex items-center gap-2 pb-2 border-b border-gray-200">
            <Phone className="w-5 h-5 text-secondary" />
            Contact Information
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-600">Contact Number</p>
                <p className="text-sm sm:text-base font-medium text-secondary">{owner.contact || '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-600">Email</p>
                <p className="text-sm sm:text-base font-medium text-secondary break-all">{owner.email || '—'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4 flex items-center gap-2 pb-2 border-b border-gray-200">
            <DollarSign className="w-5 h-5 text-secondary" />
            Business Information
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            <div>
              <p className="text-xs text-gray-600">GST Number</p>
              <p className="text-sm sm:text-base font-medium text-secondary">{owner.gst || '—'}</p>
            </div>
            {owner.owner_percentage != null && (
              <div>
                <p className="text-xs text-gray-600">Owner Percentage</p>
                <p className="text-sm sm:text-base font-medium text-secondary">{owner.owner_percentage}%</p>
              </div>
            )}
          </div>
        </div>

        {/* Address */}
        {owner.address && (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
            <h2 className="text-lg font-bold text-secondary mb-4 flex items-center gap-2 pb-2 border-b border-gray-200">
              <MapPin className="w-5 h-5 text-secondary" />
              Address
            </h2>
            <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap">{owner.address}</p>
          </div>
        )}

        {/* Vehicles */}
        {owner.vehicles_data && owner.vehicles_data.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
            <h2 className="text-lg font-bold text-secondary mb-4 pb-2 border-b border-gray-200">
              Vehicles ({owner.vehicles_data.length})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-0 md:divide-y md:divide-gray-100">
              {owner.vehicles_data.map((v, idx) => (
                <div key={v.id || idx} className="flex items-center justify-between p-2 md:py-3 border border-gray-100 md:border-0 rounded-lg md:rounded-none bg-gray-50 md:bg-transparent">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary truncate">{v.vehicle_number}</p>
                    <p className="text-xs text-gray-500 truncate">{v.vehicle_name}</p>
                  </div>
                  <span className="text-xs text-gray-400 font-mono shrink-0 ml-2">#{v.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4 pb-2 border-b border-gray-200">
            Metadata
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            <div>
              <p className="text-xs text-gray-600">Created</p>
              <p className="text-sm font-medium text-secondary">{formatDate(owner.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Last Updated</p>
              <p className="text-sm font-medium text-secondary">{formatDate(owner.updated_at)}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="primary"
            icon={Pencil}
            onClick={() => navigate(`/vehicle-owners/edit/${vehicleOwnerId}`)}
          >
            Edit Owner
          </Button>
          <Button variant="secondary" onClick={() => navigate('/vehicle-owners')}>
            Back to List
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewVehicleOwner;
